import type { GameRow } from './api';

export const names = (g: Pick<GameRow, 'white' | 'black'>) => `${g.white ?? '?'} – ${g.black ?? '?'}`;

/** Volle Züge aus Halbzügen: 33 Halbzüge = 17 Züge */
export const fullMoves = (plies: number) => Math.ceil(plies / 2);

export function debounce<A extends unknown[]>(f: (...a: A) => void, ms: number) {
  let t: number | undefined;
  return (...a: A) => {
    clearTimeout(t);
    t = window.setTimeout(() => f(...a), ms);
  };
}

/** Öffentliche Demo (Server mit -Dschachserver.public=true): nur lesen, Figurenwahl je Browser */
export let isPublic = false;
/** höchste Suchtiefe der Live-Analyse, die der Server zulässt (Demo: niedriger) */
export let maxDepth = 22;
export async function loadMode() {
  try {
    const m = await (await fetch('/api/mode')).json();
    isPublic = m.public === true;
    if (typeof m.maxDepth === 'number') maxDepth = m.maxDepth;
  } catch {
    // alter Server ohne /api/mode: Einzelplatz
  }
}

/**
 * Figurensatz für alle Module (Analyse, Spielen, Editor, Foto, Wallpaper). Er liegt auf dem Server,
 * damit auch der Wallpaper-Renderer ihn nutzt; im Browser nur als Zwischenspeicher fürs erste Bild.
 */
export type AppPieces = 'genelec' | 'cburnett';
let pieces: AppPieces = (() => {
  try {
    return localStorage.getItem('schachserver.pieces') === 'cburnett' ? 'cburnett' : 'genelec';
  } catch {
    return 'genelec';
  }
})();

export function appPieces(): AppPieces {
  return pieces;
}

function remember(p: AppPieces) {
  pieces = p;
  try {
    localStorage.setItem('schachserver.pieces', p);
  } catch {
    // ohne Speicher: gilt trotzdem, weil der Server die Wahl hält
  }
}

async function send(method: 'GET' | 'PUT', body?: AppPieces): Promise<AppPieces> {
  const res = await fetch('/api/pieces', {
    method,
    headers: body ? { 'X-Schachserver': '1', 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify({ pieces: body }) : undefined,
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return ((await res.json()).pieces === 'cburnett' ? 'cburnett' : 'genelec');
}

/**
 * Beim Start: Stand vom Server holen. Einmalige Übernahme aus der früheren reinen Browser-Einstellung
 * (schachserver.pieces ohne Marker "schachserver.piecesSynced"), damit eine getroffene Wahl nicht verloren geht.
 */
export async function loadPieces(redraw: () => void) {
  if (isPublic) return; // Demo: Wahl bleibt im Browser
  try {
    let synced = false;
    try {
      synced = localStorage.getItem('schachserver.piecesSynced') === '1';
    } catch {}
    const stored = (() => {
      try {
        return localStorage.getItem('schachserver.pieces');
      } catch {
        return null;
      }
    })();
    const p = !synced && (stored === 'cburnett' || stored === 'genelec') ? await send('PUT', stored) : await send('GET');
    try {
      localStorage.setItem('schachserver.piecesSynced', '1');
    } catch {}
    if (p !== pieces) {
      remember(p);
      redraw();
    } else remember(p);
  } catch {
    // Server nicht erreichbar: Zwischenspeicher gilt
  }
}

export async function setAppPieces(p: AppPieces, redraw: () => void) {
  remember(p);
  redraw();
  if (isPublic) return;
  try {
    remember(await send('PUT', p));
  } catch {
    // Anzeige behält die Wahl; beim nächsten Start gilt wieder der Server-Stand
  }
  redraw();
}

/**
 * Vollbild wie der Zen-Modus von lichess: nur das Brett (mit Namen und Zug), Browser im Vollbild.
 * Esc beendet ihn über die Fullscreen-API; ohne erlaubtes Vollbild bleibt es beim Ausblenden.
 */
let focus = false;
export const inFocus = () => focus;

export function setFocus(on: boolean, redraw: () => void) {
  focus = on;
  if (on) document.documentElement.requestFullscreen?.().catch(() => {});
  else if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  redraw();
}

export function watchFullscreen(redraw: () => void) {
  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement && focus) {
      focus = false;
      redraw();
    }
  });
}
